/*!
 * grit.js — fold:, clip:, ring:, comb:, crush: in a synth's voice or an fx bus
 * © 2026 Rob Canning — GPLv3
 *
 *     synth(uid:a, wave:saw, freq:55, fold:0.6)
 *     synth(uid:a, wave:noise, crush:{bits:3, rate:2000}, comb:{freq:82, fb:0.97})
 *     synth(uid:a, wave:square, freq:40, ring:follow(a.level, 30, 900), clip:0.7)
 *     fx(wall, crush:4, fold:0.4)
 *
 * The sound is gritCore.js, run in the audio engine by gritWorklet.js
 * (workletFx.js: a voice made before the engine has it plays clean until it
 * does). Any value can follow a signal: grit.set() each time it moves, the
 * core gliding there.
 */

import { gritSettings } from "./gritCore.js";
import { loadWorklet, buildWorkletFx } from "./workletFx.js";
import { isBindable, bindParam } from "../../control/paramBinding.js";

const URL_ = new URL("./gritWorklet.js", import.meta.url).href;

/** Load it into this context's audio engine (once). */
export const loadGrit = ctx => loadWorklet(ctx, URL_);

/** The keys of a line that are grit's. */
export const GRIT_KEYS = ["fold", "clip", "ring", "comb", "crush"];

/** Where a follow() can sit in each, and the range it moves in. */
const RANGES = {
  fold: [0, 1], clip: [0, 1], ring: [0, 20000], comb: [10, 10000], crush: [1, 16],
  "ring.freq": [0, 20000], "ring.mix": [0, 1],
  "comb.freq": [10, 10000], "comb.fb": [-0.99, 0.99], "comb.mix": [0, 1],
  "crush.bits": [1, 16], "crush.rate": [0, 96000],
};

/** The line's grit keys, a copy (a followed value in it changed as the signal moves). */
const gritOf = p => Object.fromEntries(GRIT_KEYS.filter(k => p[k] != null).map(k => [k, p[k] && typeof p[k] === "object" && p[k].type == null ? { ...p[k] } : p[k]]));

/** A followed value's start: the low end of its range (follow(x, lo, hi)), else the range's own. */
const startOf = (v, range) => (Array.isArray(v?.range) ? Number(v.range[0]) : range[0]);

/**
 * fold: clip: ring: comb: crush: for one voice (or, channels 2, an fx bus):
 * { input, output, set(params), bind(params) -> unbind[], stop() } -- null when
 * the line asks for none of them.
 */
export function buildGrit(ctx, params, channels = 1) {
  const current = gritOf(params);
  const settings = () => gritSettings(current, v => (isBindable(v) ? startOf(v, [0, 0]) : undefined));
  // a followed one that starts at nothing (fold:follow(x, 0, 1)) is there all the same, to come in
  const followed = Object.values(current).some(v => isBindable(v) || (v && typeof v === "object" && Object.values(v).some(isBindable)));
  const first = settings() || (followed ? { bits: 16 } : null);
  if (!first) return null;
  const fx = buildWorkletFx(ctx, { processor: "oscilla-grit", url: URL_, settings: first, channels });
  return {
    ...fx,
    /** A line run again (in place): its grit keys. */
    set(p) { Object.assign(current, gritOf(p)); fx.set(settings() || { bits: 16 }); },
    /** Every follow() in them bound: the sound changes as the signal does. */
    bind(owner = null) {
      const unbind = [];
      for (const k of GRIT_KEYS) {
        const v = current[k];
        const paths = isBindable(v) ? [[k, null]] : (v && typeof v === "object" ? Object.keys(v).filter(kk => isBindable(v[kk])).map(kk => [k, kk]) : []);
        for (const [key, sub] of paths) {
          const range = RANGES[sub ? `${key}.${sub}` : key];
          const b = bindParam(sub ? v[sub] : v, x => {
            if (sub) current[key] = { ...current[key], [sub]: x }; else current[key] = x;
            fx.set(settings() || { bits: 16 });
          }, { min: range[0], max: range[1], default: range[0], ...(owner ? { owner: `${owner}:${key}${sub ? "." + sub : ""}` } : {}) });
          if (b?.unbind) unbind.push(b.unbind);
        }
      }
      return unbind;
    },
  };
}
